import React, {useState, useEffect, useCallback} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  Chip
} from '@mui/material';
import { CheckCircle as CheckIcon } from '@mui/icons-material';
import { SubscriptionOfferService } from '../Services/SubscriptionOfferService';

const InviteSignUp = () => {
  const { inviteCode } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [offerDetails, setOfferDetails] = useState(null);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    acceptTerms: false
  });
  const [submitting, setSubmitting] = useState(false);

  const loadOfferDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await SubscriptionOfferService.getOfferDetails(inviteCode);

      if (response.status === 'success' && response.offer) {
        const offer = response.offer;
        setOfferDetails(offer);

        // Pre-fill email if provided in the offer
        if (offer.recipientEmail) {
          setFormData(prev => ({ ...prev, email: offer.recipientEmail }));
        }

        // DO NOT pre-fill name - user will provide it during sign-up
        // Name from offer is not shown until account is created via email confirmation

        if (!offer.isValid) {
          if (offer.isExpired) {
            setError('This invitation has expired.');
          } else {
            setError('This invitation is no longer valid.');
          }
        }
      } else {
        setError('Invalid invitation code.');
      }
    } catch (err) {
      console.error('Error loading offer:', err);
      setError('Failed to load invitation details. Please check the link and try again.');
    } finally {
      setLoading(false);
    }
  }, [inviteCode]);
  
  useEffect(() => {
    loadOfferDetails();
  }, [loadOfferDetails]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.acceptTerms) {
      setError('Please accept the terms and conditions to continue.');
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      // Validate the offer
      await SubscriptionOfferService.validateOffer(inviteCode);

      // Here you would typically:
      // 1. Create the user account
      // 2. Set up the subscription with GoCardless
      // 3. Link everything together
      
      // For now, redirect to GoCardless setup or success page
      // This is a placeholder - integrate with your actual signup flow
      navigate(`/signup/success?offer=${inviteCode}`);
      
    } catch (err) {
      console.error('Error during signup:', err);
      setError(err.message || 'Failed to process signup. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#0f172a'
        }}
      >
        <CircularProgress sx={{ color: '#10b981' }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#0f172a',
        py: 4
      }}
    >
      <Card
        sx={{
          maxWidth: 600,
          width: '100%',
          mx: 2,
          backgroundColor: '#1e293b',
          color: 'white'
        }}
      >
        <CardContent sx={{ p: 4 }}>
          {error ? (
            <Box>
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={() => navigate('/')}
              >
                Return to Home
              </Button>
            </Box>
          ) : offerDetails ? (
            <Box>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <CheckIcon sx={{ fontSize: 60, color: '#10b981', mb: 2 }} />
                <Typography variant="h4" gutterBottom>
                  You're Invited!
                </Typography>
                <Typography variant="body1" sx={{ color: '#94a3b8' }}>
                  {offerDetails.venueName} has invited you to join with a special subscription offer.
                </Typography>
              </Box>

              <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.12)' }} />

              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Subscription Details
                </Typography>
                <Box sx={{ backgroundColor: '#0f172a', p: 2, borderRadius: 1, mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {offerDetails.subscriptionName}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Chip
                      label={`${offerDetails.tokens} Tokens`}
                      color="primary"
                      size="small"
                    />
                    <Chip
                      label={`£${offerDetails.cycleCost} per ${offerDetails.cycleType}`}
                      color="success"
                      size="small"
                    />
                  </Box>
                </Box>
              </Box>

              <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.12)' }} />

              <form onSubmit={handleSubmit}>
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                  Your Details
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <TextField
                    label="First Name"
                    fullWidth
                    required
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    InputProps={{ sx: { color: 'white' } }}
                    InputLabelProps={{ sx: { color: 'white' } }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' },
                        '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.4)' }
                      }
                    }}
                  />
                  <TextField
                    label="Last Name"
                    fullWidth
                    required
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    InputProps={{ sx: { color: 'white' } }}
                    InputLabelProps={{ sx: { color: 'white' } }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' },
                        '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.4)' }
                      }
                    }}
                  />
                </Box>

                <TextField
                  label="Email"
                  type="email"
                  fullWidth
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  disabled={!!offerDetails.recipientEmail}
                  sx={{ mb: 2 }}
                  InputProps={{ sx: { color: 'white' } }}
                  InputLabelProps={{ sx: { color: 'white' } }}
                />

                <TextField
                  label="Phone Number"
                  type="tel"
                  fullWidth
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  sx={{ mb: 3 }}
                  InputProps={{ sx: { color: 'white' } }}
                  InputLabelProps={{ sx: { color: 'white' } }}
                />

                <Box sx={{ mb: 3, p: 2, backgroundColor: '#0f172a', borderRadius: 1 }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.acceptTerms}
                      onChange={(e) => setFormData({ ...formData, acceptTerms: e.target.checked })}
                      style={{ marginTop: '4px', marginRight: '8px' }}
                    />
                    <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                      I accept the terms and conditions of {offerDetails.venueName} and agree to the subscription terms.
                    </Typography>
                  </label>
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  size="large"
                  disabled={submitting || !formData.acceptTerms}
                  sx={{ mb: 2 }}
                >
                  {submitting ? <CircularProgress size={24} /> : 'Continue to Payment Setup'}
                </Button>

                <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: '#94a3b8' }}>
                  You will be redirected to GoCardless to set up your payment method.
                </Typography>
              </form>
            </Box>
          ) : null}
        </CardContent>
      </Card>
    </Box>
  );
};

export default InviteSignUp;
